import { useEffect, useState } from "react";
import {
  FileText,
  Phone,
  User,
  Calendar,
  MapPin,
  Send,
  RotateCcw,
  CreditCard,
  AlertCircle,
} from "lucide-react";
import axios from "axios";
import toast from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import "./ExperianCibil.css";
import { fetchUserProfile } from "../redux/slices/branch/userProfileSlice";
import { fetchKey } from "../redux/slices/keySlice";
import { fetchCompanyAssignedApis } from "../redux/slices/assignedApiSlice";
import ExperianPdfDocument from "./ExperianPdfDocument";
import { PDFDownloadLink } from "@react-pdf/renderer";

const initialFormState = {
  mobile: "",
  panNumber: "",
  firstName: "",
  lastName: "",
  dob: "",
  pincode: "",
};

// const initialFormState = {
//   mobile: "7972425758",
//   panNumber: "DWTPM4947Q",
//   firstName: "DEVIKA MOHOD",
//   lastName: "VINOD",
//   dob: "1999-04-17",
//   pincode: "425508",
// };

const ExperianCibil = () => {
  const { profile } = useSelector((state) => state.userProfile);
  const { keyData } = useSelector((state) => state.key);
  const { assignedApis } = useSelector((state) => state.assignedApi || {});
  const dispatch = useDispatch();

  const [formData, setFormData] = useState(initialFormState);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [apiResponse, setApiResponse] = useState(null);
  const [company, setCompany] = useState(null);

  // Fetch user profile on mount
  useEffect(() => {
    dispatch(fetchUserProfile());
  }, [dispatch]);

  // Fetch key & assigned apis only when superAdminId exists
  useEffect(() => {
    if (profile?.superAdminId) {
      dispatch(fetchKey(profile.superAdminId));
      dispatch(fetchCompanyAssignedApis(profile.superAdminId));
    }
  }, [dispatch, profile?.superAdminId]);

  useEffect(() => {
    const fetchCompany = async () => {
      try {
        const res = await fetch(
          `https://payyug.in/api/get-company?superAdminId=${profile?.superAdminId}`,
        );
        const data = await res.json();
        if (res.ok && data) {
          if (data.logo) {
            data.logoUrl = `https://payyug.in/${data.logo.replace(/^images\//, "")}`;
          }
          if (data.qrCodeImage) {
            data.qrCodeUrl = `https://payyug.in/${data.qrCodeImage.replace(/^images\//, "")}`;
          }
          setCompany(data);
        }
      } catch (err) {
        console.error("Error fetching company:", err);
      }
    };

    if (profile?.superAdminId) {
      fetchCompany();
    }
  }, [profile?.superAdminId]);

  // Handle Form Inputs
  const handleChange = (e) => {
    const { name, value } = e.target;
    let formattedValue = value;

    if (name === "panNumber") {
      formattedValue = value.toUpperCase().trim();
    }

    if (name === "mobile" || name === "pincode") {
      formattedValue = value.replace(/\D/g, "");
    }

    setFormData((prev) => ({
      ...prev,
      [name]: formattedValue,
    }));

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  // Form Validation
  const validateForm = () => {
    const newErrors = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = "First name is required";
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = "Last name is required";
    }

    if (!formData.mobile) {
      newErrors.mobile = "Mobile number is required";
    } else if (!/^[6-9]\d{9}$/.test(formData.mobile)) {
      newErrors.mobile = "Enter a valid 10-digit mobile number";
    }

    if (!formData.panNumber) {
      newErrors.panNumber = "PAN number is required";
    } else if (!/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(formData.panNumber)) {
      newErrors.panNumber = "Invalid PAN format (e.g. ABCDE1234F)";
    }

    if (!formData.dob) {
      newErrors.dob = "Date of Birth is required";
    }

    if (!formData.pincode) {
      newErrors.pincode = "Pincode is required";
    } else if (!/^\d{6}$/.test(formData.pincode)) {
      newErrors.pincode = "Enter a valid 6-digit pincode";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // 1. Service Name Check ko Flexible Banayein
  const isServiceActive = (serviceKey) => {
    if (!Array.isArray(assignedApis)) return true;

    const service = assignedApis?.find((item) =>
      item?.serviceName?.toLowerCase()?.includes(serviceKey.toLowerCase()),
    );

    return Boolean(
      service &&
      (service.isEnabled === true ||
        String(service.status).toLowerCase() === "active"),
    );
  };

  const isExperianActive = isServiceActive("EXPERIAN_API");

  // 2. Submit Handler
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error("Please fill all required fields correctly.");
      return;
    }

if (!keyData || !keyData.apiKey || !keyData.secretKeyHash) {
  toast.error("API Keys missing or invalid. Please check your account settings.");
  return;
}

    setIsLoading(true);
    setApiResponse(null);

    try {
      const payload = {
        mobile: formData.mobile,
        panNumber: formData.panNumber,
        firstName: formData.firstName,
        lastName: formData.lastName,
        dob: formData.dob,
        pincode: formData.pincode,
        customerId: 10,
        employeId: 5,
      };

      const response = await axios.post(
        `https://payyug.in/api/apicheck/cibil-details`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
            "x-secret-key": keyData.secretKeyHash,
            "x-api-key": keyData.apiKey,
          },
        },
      );

      const resData = response?.data;
      const isSuccess =
        resData?.status === "SUCCESS" ||
        resData?.success === true ||
        resData?.data?.status === 200 ||
        resData?.data?.status === "SUCCESS" ||
        resData?.data?.statusCode === 200;

      if (isSuccess) {
        toast.success(
          resData?.message ||
            resData?.data?.message ||
            "Report fetched successfully!",
        );
        setApiResponse(resData);
      } else {
        toast.error(
          resData?.message ||
            resData?.data?.message ||
            "Failed to fetch report.",
        );
        setApiResponse(resData);
      }
    } catch (error) {
      console.error("Experian Credit API Error:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Something went wrong while fetching the report.";

      toast.error(errorMessage);
      setApiResponse({
        status: "FAILED",
        message: errorMessage,
        error: error.response?.data || null,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Reset Form
  const handleReset = () => {
    setFormData({
      mobile: "",
      panNumber: "",
      firstName: "",
      lastName: "",
      dob: "",
      pincode: "",
    });
    setErrors({});
    setApiResponse(null);
  };

  const isResponseSuccess =
    apiResponse?.status === "SUCCESS" ||
    apiResponse?.success === true ||
    apiResponse?.data?.status === 200 ||
    apiResponse?.data?.status === "SUCCESS" ||
    apiResponse?.data?.statusCode === 200;

  const applicantName =
    apiResponse?.data?.result?.Current_Application?.Current_Application_Details
      ?.Current_Applicant_Details?.First_Name || "Credit_Report";

  return (
    <div className="exp-credit-container">
      <div className="exp-credit-grid-wrapper">
        {/* Left Section: Form */}
        <div className="exp-credit-form-card">
          <div className="exp-credit-card-header">
            <div className="exp-credit-header-group">
              <div className="exp-credit-icon-badge">
                <FileText size={20} />
              </div>
              <div>
                <h3 className="exp-credit-heading">
                  Check Credit Score / Experian Credit Report
                </h3>
                <p className="exp-credit-subheading">
                  Enter customer details to fetch real-time credit score.
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={handleReset}
              className="exp-credit-btn-reset"
            >
              <RotateCcw size={13} />
              <span>Reset</span>
            </button>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="exp-credit-form-grid">
              {/* First Name */}
              <div className="exp-credit-field-group">
                <label className="exp-credit-label">
                  First Name{" "}
                  <span className="exp-credit-required-indicator">*</span>
                </label>
                <div className="exp-credit-input-container">
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    placeholder="Enter first name"
                    className={`exp-credit-input-field exp-credit-input-field--uppercase ${
                      errors.firstName
                        ? "exp-credit-input-field--has-error"
                        : ""
                    }`}
                  />
                  <User size={15} className="exp-credit-input-adornment" />
                </div>
                {errors.firstName && (
                  <p className="exp-credit-validation-message">
                    {errors.firstName}
                  </p>
                )}
              </div>

              {/* Last Name */}
              <div className="exp-credit-field-group">
                <label className="exp-credit-label">
                  Last Name{" "}
                  <span className="exp-credit-required-indicator">*</span>
                </label>
                <div className="exp-credit-input-container">
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    placeholder="Enter last name"
                    className={`exp-credit-input-field exp-credit-input-field--uppercase ${
                      errors.lastName ? "exp-credit-input-field--has-error" : ""
                    }`}
                  />
                  <User size={15} className="exp-credit-input-adornment" />
                </div>
                {errors.lastName && (
                  <p className="exp-credit-validation-message">
                    {errors.lastName}
                  </p>
                )}
              </div>

              {/* Mobile Number */}
              <div className="exp-credit-field-group">
                <label className="exp-credit-label">
                  Mobile Number{" "}
                  <span className="exp-credit-required-indicator">*</span>
                </label>
                <div className="exp-credit-input-container">
                  <input
                    type="text"
                    name="mobile"
                    maxLength={10}
                    value={formData.mobile}
                    onChange={handleChange}
                    placeholder="Enter 10-digit mobile number"
                    className={`exp-credit-input-field ${
                      errors.mobile ? "exp-credit-input-field--has-error" : ""
                    }`}
                  />
                  <Phone size={15} className="exp-credit-input-adornment" />
                </div>
                {errors.mobile && (
                  <p className="exp-credit-validation-message">
                    {errors.mobile}
                  </p>
                )}
              </div>

              {/* PAN Number */}
              <div className="exp-credit-field-group">
                <label className="exp-credit-label">
                  PAN Number{" "}
                  <span className="exp-credit-required-indicator">*</span>
                </label>
                <div className="exp-credit-input-container">
                  <input
                    type="text"
                    name="panNumber"
                    maxLength={10}
                    value={formData.panNumber}
                    onChange={handleChange}
                    placeholder="e.g. DWTPM4947Q"
                    className={`exp-credit-input-field exp-credit-input-field--uppercase exp-credit-input-field--monospace ${
                      errors.panNumber
                        ? "exp-credit-input-field--has-error"
                        : ""
                    }`}
                  />
                  <CreditCard
                    size={15}
                    className="exp-credit-input-adornment"
                  />
                </div>
                {errors.panNumber && (
                  <p className="exp-credit-validation-message">
                    {errors.panNumber}
                  </p>
                )}
              </div>

              {/* Date of Birth */}
              <div className="exp-credit-field-group">
                <label className="exp-credit-label">
                  Date of Birth{" "}
                  <span className="exp-credit-required-indicator">*</span>
                </label>
                <div className="exp-credit-input-container">
                  <input
                    type="date"
                    name="dob"
                    value={formData.dob}
                    onChange={handleChange}
                    className={`exp-credit-input-field ${
                      errors.dob ? "exp-credit-input-field--has-error" : ""
                    }`}
                  />
                  <Calendar size={15} className="exp-credit-input-adornment" />
                </div>
                {errors.dob && (
                  <p className="exp-credit-validation-message">{errors.dob}</p>
                )}
              </div>

              {/* Pincode */}
              <div className="exp-credit-field-group">
                <label className="exp-credit-label">
                  Pincode{" "}
                  <span className="exp-credit-required-indicator">*</span>
                </label>
                <div className="exp-credit-input-container">
                  <input
                    type="text"
                    name="pincode"
                    maxLength={6}
                    value={formData.pincode}
                    onChange={handleChange}
                    placeholder="6-digit pincode"
                    className={`exp-credit-input-field ${
                      errors.pincode ? "exp-credit-input-field--has-error" : ""
                    }`}
                  />
                  <MapPin size={15} className="exp-credit-input-adornment" />
                </div>
                {errors.pincode && (
                  <p className="exp-credit-validation-message">
                    {errors.pincode}
                  </p>
                )}
              </div>
            </div>

            {/* Warning Banner when Access is Disabled */}
            {!isExperianActive && (
              <div className="exp-credit-access-alert">
                <AlertCircle size={16} />
                <span>
                  Experian Credit API is not enabled for your account. Please
                  contact admin.
                </span>
              </div>
            )}

            {/* Submit Action */}
            <button
              type="submit"
              disabled={isLoading || !isExperianActive}
              className="exp-credit-btn-submit"
              title={
                !isExperianActive ? "You don't have access to this API" : ""
              }
            >
              {isLoading ? (
                <span>Fetching Experian Credit Report...</span>
              ) : !isExperianActive ? (
                <span>Service Access Disabled</span>
              ) : (
                <>
                  <Send size={15} />
                  <span>Fetch Experian Credit Report</span>
                </>
              )}
            </button>
          </form>
        </div>

        {/* Right Section: API Output Card */}
        <div>
          {apiResponse && (
            <div className="exp-credit-response-card">
              <div className="exp-credit-card-header exp-credit-card-header--compact">
                <span className="exp-credit-result-header-text">
                  API Response Result
                </span>
                <div className="exp-credit-action-cluster">
                  {apiResponse?.data && isResponseSuccess && (
                    <PDFDownloadLink
                      document={
                        <ExperianPdfDocument
                          reportData={apiResponse}
                          company={company}
                        />
                      }
                      fileName={`Experian_Credit_Report_${applicantName}.pdf`}
                      className="exp-credit-btn-download"
                    >
                      {({ loading }) =>
                        loading ? "Generating PDF..." : "Download PDF"
                      }
                    </PDFDownloadLink>
                  )}
                  <span
                    className={`exp-credit-pill-tag ${
                      isResponseSuccess
                        ? "exp-credit-pill-tag--success"
                        : "exp-credit-pill-tag--failed"
                    }`}
                  >
                    {isResponseSuccess ? "Success" : "Failed"}
                  </span>
                </div>
              </div>

              {/* Dynamic Score Display */}
              <div
                className={`exp-credit-score-highlight ${
                  isResponseSuccess
                    ? "exp-credit-score-highlight--success"
                    : "exp-credit-score-highlight--failed"
                }`}
              >
                {isResponseSuccess ? (
                  <>
                    <span className="exp-credit-score-caption">
                      Credit / CRIF Score
                    </span>
                    <h2 className="exp-credit-score-number">
                      {apiResponse?.data?.result?.SCORE?.FCIREXScore || "-1"}
                    </h2>
                    <span className="exp-credit-score-note">
                      {apiResponse.data?.message ||
                        apiResponse?.message ||
                        "Report generated successfully"}
                    </span>
                  </>
                ) : (
                  <>
                    <AlertCircle size={32} className="exp-credit-alert-icon" />
                    <h2 className="exp-credit-alert-message">
                      {apiResponse?.message ||
                        apiResponse?.data?.message ||
                        "Verification Failed"}
                    </h2>
                  </>
                )}
              </div>

              {/* JSON Output Terminal */}
              {/* <div className="exp-credit-json-terminal">
                <pre className="exp-credit-code-block">
                  {JSON.stringify(apiResponse, null, 2)}
                </pre>
              </div> */}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExperianCibil;
